import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { NotificationItem } from './NotificationItem';
import { Bell, Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import type { NotificationType } from '../../types/notifications';

const convertFirebaseTimestamp = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  
  if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
    try {
      return timestamp.toDate();
    } catch (e) {
      console.error('Error converting Firestore timestamp:', e);
      return null;
    }
  }
  
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  
  if (typeof timestamp === 'number') {
    if (timestamp < 1e12) timestamp *= 1000;
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
};

interface Notification {
  id: string;
  metadata: NotificationType['metadata'];
  createdAt: Date;
  readAt: Date | null;
  priority: string;
  recipientId: string;
}

export const NotificationCenter: React.FC = () => {
  const { user } = useAuth();
  const { features } = useFeatureFlags();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!user || !features.notifications) {
      setLoading(false);
      return;
    }

    const initializeNotifications = async () => {
      try {
        setLoading(true);
        setError(null);

        const notificationsRef = collection(db, 'notifications');
        const q = query(
          notificationsRef,
          where('recipientId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );

        console.log('Starting notification listener for user:', user.uid);

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            try {
              const notificationsData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  metadata: data.metadata,
                  createdAt: convertFirebaseTimestamp(data.createdAt) || new Date(),
                  readAt: convertFirebaseTimestamp(data.readAt),
                  priority: data.priority || 'low',
                  recipientId: data.recipientId
                } as Notification;
              });

              const validNotifications = notificationsData.filter(notification => {
                const isValid = notification.metadata && typeof notification.metadata === 'object' && 'type' in notification.metadata;
                if (!isValid) {
                  console.warn('Invalid notification found:', notification);
                }
                return isValid;
              });

              setNotifications(validNotifications);
              setError(null);
              setInitialized(true);
            } catch (err) {
              console.error('Error processing notifications:', err);
              setError('Fehler beim Verarbeiten der Benachrichtigungen');
            } finally {
              setLoading(false);
            }
          },
          (err) => {
            console.error('Error loading notifications:', err);
            setError('Fehler beim Laden der Benachrichtigungen');
            setLoading(false);
          }
        );

        return () => {
          unsubscribe();
          setInitialized(false);
        };
      } catch (err) {
        console.error('Error initializing notifications:', err);
        setError('Fehler beim Initialisieren der Benachrichtigungen');
        setLoading(false);
      }
    };

    initializeNotifications();
  }, [user, features.notifications]);

  const markAsRead = async (notificationId: string) => {
    if (!user || !initialized) return;

    try {
      console.log('Marking notification as read:', notificationId);
      await updateDoc(doc(db, 'notifications', notificationId), {
        readAt: new Date()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      setError('Benachrichtigung konnte nicht als gelesen markiert werden');
    }
  };

  const deleteNotification = async (notificationId: string) => {
    if (!user) return;

    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      console.log('Benachrichtigung gelöscht:', notificationId);
    } catch (error) {
      console.error('Fehler beim Löschen der Benachrichtigung:', error);
      setError('Benachrichtigung konnte nicht gelöscht werden');
    }
  };

  const deleteAllNotifications = async () => {
    if (!user || notifications.length === 0) return;

    try {
      const batch = writeBatch(db);
      notifications.forEach((notification) => {
        const notificationRef = doc(db, 'notifications', notification.id);
        batch.delete(notificationRef);
      });

      await batch.commit();
      console.log('Alle Benachrichtigungen gelöscht');
    } catch (error) {
      console.error('Fehler beim Löschen aller Benachrichtigungen:', error);
      setError('Benachrichtigungen konnten nicht gelöscht werden');
    }
  };

  if (!features.notifications) {
    return (
      <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
              Benachrichtigungen deaktiviert
            </h3>
            <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-200">
              Das Benachrichtigungssystem ist derzeit deaktiviert.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !initialized) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
        <p className="mt-4 text-gray-600 dark:text-gray-400">
          Benachrichtigungen werden geladen...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
              Fehler beim Laden
            </h3>
            <p className="mt-2 text-sm text-red-700 dark:text-red-200">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.readAt).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Bell className="h-6 w-6 mr-2" />
          Benachrichtigungen
          {unreadCount > 0 && (
            <span className="ml-2 px-2.5 py-0.5 text-sm bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded-full">
              {unreadCount} neu
            </span>
          )}
        </h2>
        {notifications.length > 0 && (
          <button
            onClick={deleteAllNotifications}
            className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex items-center"
            title="Alle Benachrichtigungen löschen"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Alle löschen
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          <Bell className="h-12 w-12 mb-4" />
          <p>Keine Benachrichtigungen vorhanden</p>
        </div>
      ) : (
        <div className="space-y-4">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              id={notification.id}
              metadata={notification.metadata}
              createdAt={notification.createdAt}
              readAt={notification.readAt}
              priority={notification.priority}
              onMarkAsRead={markAsRead}
              onDelete={deleteNotification}
            />
          ))}
        </div>
      )}
    </div>
  );
};
